























































import { Component, Vue, Prop, Watch } from 'vue-property-decorator'
import { ITemplate, ITemplateLine } from '@/store/modules/templatesaver'
import { v1 as uuidv1 } from 'uuid'
import draggable from 'vuedraggable'
import * as DeepEqual from 'deep-equal'
import { USAGE_STAT_FEATURES } from '@/system/usage-stats'

const NOTIFICATION_DURATION = 2000

@Component({
  components: {
    draggable,
    TemplateLine: require('./TemplateLine').default
  }
})
export default class TemplateSaveModal extends Vue {
  @Prop({
    required: true
  })
  target: string

  @Prop({
    required: true
  })
  rules: any[]

  @Prop({
    required: true
  })
  name: string

  selectedTemplateId: string | null = null
  modalOpen: boolean = false
  loading: boolean = false
  templateLines: ITemplateLine[] = []
  hasUnsavedChanges: boolean = false

  dragOptions = {
    animation: 200,
    group: 'description',
    disabled: false,
    ghostClass: 'ghost'
  }

  get disabled(): boolean {
    return this.templateLines.some(x => !x.line.name)
  }

  get selectedTemplateLine(): ITemplateLine | undefined {
    return this.templateLines.find(x => x.line.id === this.selectedTemplateId)
  }

  get templates(): ITemplate[] {
    const templates = this.$store.state.templatesaver[this.name][this.target]

    if (templates) return templates

    return []
  }

  init() {
    this.templateLines = JSON.parse(JSON.stringify(this.templates.map(x => {
      return {
        line: x
      } as ITemplateLine
    })))
  }

  open() {
    this.modalOpen = true
  }

  cancel() {
    this.modalOpen = false

    this.init()
  }

  addTemplate() {
    this.templateLines.push({
      line: {
        id: uuidv1(),
        name: '',
        rules: []
      }
    })
  }

  removeTemplate(template: ITemplate) {
    const index = this.templateLines.findIndex(x => x.line.id === template.id)

    if (index > -1) {
      this.templateLines.splice(index, 1)
    }
  }

  onTemplateChange() {
    if (this.selectedTemplateLine) {
      this.$emit('change', this.selectedTemplateLine.line.rules)
    } else {
      this.selectedTemplateId = null
    }
  }

  async saveToServer() {
    this.loading = true

    await this.$store.dispatch('templatesaver/save', this.name)

    this.loading = false
  }

  async saveTemplate() {
    if (this.loading) return

    // User had no templates, so save as default template name
    if (this.selectedTemplateId === null) {
      const defaultTemplatePrefix: string = 'Template '
      const regex = new RegExp(`^${defaultTemplatePrefix}\\d+$`)

      const highestTemplateNumber: number = this.templateLines.filter(x => regex.test(x.line.name)).reduce((acc, val) => {
        const n = parseInt(val.line.name.split(' ')[1])

        if (n > acc) return n
        else return acc
      }, 0)

      const defaultTemplateName: string = defaultTemplatePrefix + (highestTemplateNumber + 1)
      const id = uuidv1()

      this.$store.commit('templatesaver/addOrUpdateTemplate', {
        name: this.name,
        target: this.target,
        template: {
          id,
          name: defaultTemplateName,
          rules: this.rules
        } as ITemplate
      })

      this.$buefy.notification.open({
        duration: NOTIFICATION_DURATION,
        message: `Created new template: ${defaultTemplateName}`
      })

      this.selectedTemplateId = id
    } else {
      this.$store.commit('templatesaver/addOrUpdateTemplate', {
        name: this.name,
        target: this.target,
        template: {
          id: this.selectedTemplateId,
          rules: this.rules
        } as ITemplate
      })

      this.$buefy.notification.open({
        duration: NOTIFICATION_DURATION,
        message: `Saved template`
      })
    }

    this.init()

    await this.saveToServer()

    this.checkForUnsavedChanges()
  }

  async saveAllTemplates() {
    if (this.loading) return

    this.$store.commit('templatesaver/setTemplates', {
      target: this.target,
      name: this.name,
      templates: this.templateLines.map(x => x.line)
    })

    if (!this.selectedTemplateLine) {
      this.selectedTemplateId = null
    }

    this.init()

    await this.saveToServer()

    this.$buefy.notification.open({
      duration: NOTIFICATION_DURATION,
      message: `Saved all templates`
    })

    window.addUsageStat(USAGE_STAT_FEATURES.TemplateModal, this.target)

    this.modalOpen = false
  }

  checkForUnsavedChanges() {
    if (!this.selectedTemplateId) {
      this.hasUnsavedChanges = false
      return
    }

    const storeTemplate = this.templates.find(x => x.id === this.selectedTemplateId)

    if (!storeTemplate) {
      this.hasUnsavedChanges = false
      return
    }

    this.hasUnsavedChanges = !DeepEqual(this.rules, storeTemplate.rules)
  }

  created() {
    this.$store.commit('templatesaver/initTemplate', {
      target: this.target,
      name: this.name
    })

    this.init()
  }

  @Watch('rules', { deep: true })
  watchRules() {
    this.checkForUnsavedChanges()
  }
}
